const PRINT_WIDTH = 80;
const INDENT = "  ";

const VOID = new Set([
	"area",
	"base",
	"br",
	"col",
	"embed",
	"hr",
	"img",
	"input",
	"link",
	"meta",
	"param",
	"source",
	"track",
	"wbr",
	"!doctype",
]);

const RAW = new Set(["script", "style", "pre", "textarea"]);

/** Format cloned source the way an editor Prettier pass would. */
export function prettySource(content: string, path: string): string {
	const ext = extensionOf(path);
	switch (ext) {
		case "json":
			return prettyJson(content);
		case "astro":
			return prettyAstro(content);
		case "html":
		case "svg":
		case "xml":
			return prettyHtml(content);
		default:
			return content;
	}
}

function extensionOf(path: string): string {
	const name = path.split("/").pop() ?? path;
	const dot = name.lastIndexOf(".");
	return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

function prettyJson(content: string): string {
	try {
		return `${JSON.stringify(JSON.parse(content), null, 2)}\n`;
	} catch {
		return content;
	}
}

function prettyAstro(content: string): string {
	const match = content.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/);
	if (!match) return prettyHtml(content);
	const frontmatter = prettyFrontmatter(match[1] ?? "");
	const template = prettyHtml(content.slice(match[0].length));
	return `---\n${frontmatter}\n---\n${template}`;
}

function prettyFrontmatter(source: string): string {
	return source
		.replace(/const (\w+) = (\{[\s\S]*?\});/g, (all, name: string, json: string) => {
			try {
				return `const ${name} = ${JSON.stringify(JSON.parse(json), null, 2)};`;
			} catch {
				return all;
			}
		})
		.replace(/\s+$/g, "");
}

function prettyHtml(source: string): string {
	const { html, blocks } = extractRaw(source.trim());
	const tokens = html.match(/<!--[\s\S]*?-->|<!doctype[^>]*>|<\/?[^>]+>|[^<]+/gi) ?? [];
	const lines: string[] = [];
	let depth = 0;

	const write = (text: string) => {
		if (!text) return;
		lines.push(`${INDENT.repeat(Math.max(0, depth))}${text}`);
	};

	for (let i = 0; i < tokens.length; i++) {
		const token = tokens[i] ?? "";
		if (/^<!--pretty-raw-(\d+)-->$/.test(token.trim())) {
			const index = Number(/^<!--pretty-raw-(\d+)-->$/.exec(token.trim())?.[1]);
			const block = blocks[index];
			if (block) lines.push(printRaw(block, INDENT.repeat(Math.max(0, depth))));
			continue;
		}
		if (token.startsWith("<!--") || /^<!doctype/i.test(token)) {
			write(token.trim());
			continue;
		}
		if (token.startsWith("</")) {
			depth = Math.max(0, depth - 1);
			write(formatTag(token.trim(), INDENT.repeat(depth)));
			continue;
		}
		if (token.startsWith("<")) {
			const tag = token.trim();
			const name = tagName(tag);
			const selfClose = isVoid(name) || /\/>$/.test(tag);
			const nextText = tokens[i + 1];
			const nextClose = tokens[i + 2];
			if (
				!selfClose &&
				nextText &&
				!nextText.startsWith("<") &&
				nextClose?.startsWith("</") &&
				tagName(nextClose) === name
			) {
				const text = collapseText(nextText);
				if (text && text.length <= 60) {
					write(`${formatTag(tag, INDENT.repeat(depth))}${text}</${name}>`);
					i += 2;
					continue;
				}
			}
			write(formatTag(tag, INDENT.repeat(depth)));
			if (!selfClose) depth += 1;
			continue;
		}
		const text = collapseText(token);
		if (text) write(text);
	}

	return `${lines.join("\n").replace(/\n{3,}/g, "\n\n")}\n`;
}

function extractRaw(html: string): { html: string; blocks: string[] } {
	const blocks: string[] = [];
	const next = html.replace(
		/<(script|style|pre|textarea)\b[^>]*>[\s\S]*?<\/\1>/gi,
		(full) => {
			const id = blocks.length;
			blocks.push(full);
			return `<!--pretty-raw-${id}-->`;
		},
	);
	return { html: next, blocks };
}

function printRaw(full: string, indent: string): string {
	const match = full.match(/^<([a-zA-Z][\w:-]*)([^>]*)>([\s\S]*)<\/\1>$/);
	if (!match) return `${indent}${full}`;
	const name = match[1] ?? "script";
	const attrs = match[2] ?? "";
	const body = match[3] ?? "";
	const open = formatTag(`<${name}${attrs}>`, indent);
	const close = `</${name}>`;
	const prettyBody = prettyRawBody(`${name}${attrs}`, body, indent);
	if (!prettyBody) return `${indent}${open}${close}`;
	if (!prettyBody.includes("\n") && prettyBody.length < PRINT_WIDTH) {
		return `${indent}${open}${prettyBody}${close}`;
	}
	const inner = prettyBody
		.split("\n")
		.map((line) => (line ? `${indent}${INDENT}${line}` : ""))
		.join("\n");
	return `${indent}${open}\n${inner}\n${indent}${close}`;
}

function prettyRawBody(openBits: string, body: string, _indent: string): string {
	const trimmed = body.trim();
	if (!trimmed) return "";
	if (
		/type\s*=\s*["']application\/(?:ld\+)?json["']/i.test(openBits) ||
		/id\s*=\s*["']wizard-data["']/i.test(openBits)
	) {
		try {
			return JSON.stringify(JSON.parse(trimmed), null, 2);
		} catch {
			return trimmed;
		}
	}
	return trimmed;
}

function tagName(tag: string): string {
	return tag
		.replace(/^<\/?/, "")
		.replace(/[\s/>].*$/, "")
		.toLowerCase();
}

function isVoid(name: string): boolean {
	return VOID.has(name) || RAW.has(name) === false && name.startsWith("!");
}

function formatTag(tag: string, indent: string): string {
	if (tag.length + indent.length <= PRINT_WIDTH) return tag;
	const match = tag.match(/^<(\/?[\w:.-]+)([^>]*)>$/);
	if (!match) return tag;
	const name = match[1] ?? "";
	const rest = (match[2] ?? "").trim();
	if (!rest) return tag;
	const selfClose = rest.endsWith("/");
	const attrSource = selfClose ? rest.slice(0, -1).trim() : rest;
	const attrs = splitAttrs(attrSource);
	if (attrs.length === 0) return tag;
	const inner = attrs.map((attr) => `${indent}${INDENT}${attr}`).join("\n");
	return `<${name}\n${inner}\n${indent}${selfClose ? "/>" : ">"}`;
}

function splitAttrs(source: string): string[] {
	const attrs: string[] = [];
	const re = /(?:\{[^}]+\}|[^\s"'=]+(?:=(?:"[^"]*"|'[^']*'|[^\s"'=]+))?)/g;
	let match: RegExpExecArray | null;
	while ((match = re.exec(source)) !== null) {
		const piece = match[0]?.trim();
		if (piece) attrs.push(piece);
	}
	return attrs;
}

function collapseText(value: string): string {
	const text = value.replace(/\s+/g, " ").trim();
	return text;
}
